import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calculator, Network, Server, Home, ArrowRight, Logout, 
  ChevronDown, LogOut, User, Moon, Sun, Search, Grid, 
  Activity, Bookmark, Bell, HelpCircle, Menu, X, 
  Cpu, Zap, Layers, BarChart, Globe, Cloud, Wifi
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import IPv4Calculator from './IPv4Calculator';
import IPv6Calculator from './IPv6Calculator';
import NetworkReferenceTable from './Reference';
import Avatar from '../../../services/Avatar';
import axios from 'axios';
import { generateTerminalCommand } from "./utils/terminalGenerator";



export const useSmartTerminal = (maxLines = 12) => {
  const [displayedLines, setDisplayedLines] = useState([]);

  useEffect(() => {
    const interval = setInterval(() => {
      const newCommand = generateTerminalCommand();
      setDisplayedLines((prev) => [...prev.slice(-maxLines + newCommand.length), ...newCommand]);
    }, 2500); // every 2.5s

    return () => clearInterval(interval);
  }, [maxLines]);

  return displayedLines;
};


export default function SubnetCalculator() {
  const [activeTab, setActiveTab] = useState('home');
  const [darkMode, setDarkMode] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
   const [currentUser, setCurrentUser] = useState(null);
  const navigate = useNavigate(); 
  const displayedLines = useSmartTerminal();

  // Network utilization data for visualization
  const networkData = [65, 59, 80, 81, 56, 55, 40, 75, 90, 68];

  
  const Skeleton = ({ width = "w-8", height = "h-8", rounded = "rounded-full" }) => (
    <div className={`${width} ${height} ${rounded} bg-gray-300 animate-pulse`} />
  );

  
  // Handle scroll effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
          const controller = new AbortController();
          const fetchUserProfile = async () => {
              try {
                  const response = await axios.get('/users/me', { 
                      withCredentials: true,
                      signal: controller.signal // Attach signal to cancel duplicate requests
                  });
                  setCurrentUser(response.data);
              } catch (error) {
                  if (axios.isCancel(error)) {
                      console.log('Request canceled:', error.message);
                  } else {
                      console.error('Error fetching user profile:', error);
                  }
              }
          };
      
          fetchUserProfile();
      
          return () => controller.abort(); 
      }, []);

  const handleLogout = async () => {
    try {
      const response = await fetch('/d-auth/logout', {
        method: 'POST', // or 'GET', depending on your backend implementation
        credentials: 'include', // include cookies with the request
        headers: {
          'Content-Type': 'application/json',
        },
      });
  
      if (response.ok) {       
        
        window.location.href = '/'; 
      } else {
        // If there's an error during logout
        const errorData = await response.json();
        //console.error('Logout failed', errorData);
        alert( errorData ||'Logout failed. Please try again.');
      }
    } catch (error) {
      //console.error('Error during logout:', error);
      alert('An error occurred. Please try again.');
    }
  };
  
  // Gradients for different sections
  const primaryGradient = darkMode ? 
    'from-blue-600 to-violet-600' : 
    'from-blue-500 to-purple-500';
  
  const secondaryGradient = darkMode ? 
    'from-indigo-700 to-purple-700' : 
    'from-indigo-500 to-purple-500';
  
  // Toggle theme
  const toggleTheme = () => {
    setDarkMode(!darkMode);
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'} ${darkMode ? 'text-gray-100' : 'text-gray-800'} transition-colors duration-300`}>
      {/* Navigation Bar */}
      <nav className={`${isScrolled ? 'backdrop-blur-md bg-opacity-80' : ''} ${darkMode ? 'bg-gray-900/90' : 'bg-white/90'} shadow-lg fixed top-0 w-full z-50 transition-all duration-300`}>
        <div className="container mx-auto py-4 px-6 relative">
          <div className="flex justify-between items-center">
            <motion.div 
              className="flex items-center space-x-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              onClick={() => navigate('/landing')}
            >
              <Network className={`h-7 w-7 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
              <span className={`text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r ${primaryGradient}`}>
                SubnetIQ
              </span>
            </motion.div>
            
            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center space-x-1">
              {[
                { id: 'home', icon: Home, label: 'Dashboard' },
                { id: 'ipv4', icon: Calculator, label: 'IPv4 Suite' },
                { id: 'ipv6', icon: Server, label: 'IPv6 Suite' },
                { id: 'reference', icon: Grid, label: 'References' },
              ].map((tab) => (
                <motion.button
                  key={tab.id}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
                    activeTab === tab.id 
                      ? `bg-gradient-to-r ${primaryGradient} shadow-lg shadow-blue-500/20` 
                      : `hover:bg-${darkMode ? 'gray-800' : 'gray-100'}`
                  }`}
                >
                  <tab.icon className="h-5 w-5" />
                  <span>{tab.label}</span>
                </motion.button>
              ))}
            </div>
            
            {/* Right Side Actions */}
            <div className="flex items-center space-x-2">       
              
              {/* Theme Toggle */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={toggleTheme}
                className={`p-2 rounded-full ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
              >
                {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </motion.button>
              
              
              {/* User Profile */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                className={`hidden md:flex items-center space-x-2 p-1 px-3 rounded-full border ${darkMode ? 'border-gray-700' : 'border-gray-200'} cursor-pointer`}
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-400 to-purple-500 flex items-center justify-center">
                {currentUser ? (
                      <>
                        <Avatar firstName={currentUser.first_name} lastName={currentUser.last_name} />
                      </>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Skeleton width="w-10 h-10" rounded="rounded-full" /> {/* Skeleton for Avatar */}
                        <div className="flex flex-col">
                          <Skeleton width="w-20" height="h-3" rounded="rounded-md" /> {/* Skeleton for Name */}
                          <Skeleton width="w-16" height="h-3" rounded="rounded-md" /> {/* Skeleton for Role */}
                        </div>
                      </div>
                    )}
                </div>
                <div>
                 {currentUser ? (
                                       <>                                       
                                         <div>
                                           <span className="text-sm text-gray-100 font-medium">{currentUser.first_name} {currentUser.last_name}</span>
                                           <span className="text-xs text-gray-400">{currentUser.role}</span>
                                         </div>
                                       </>
                                     ) : (
                                       <div className="flex items-center gap-2">                                         
                                         <div className="flex flex-col">
                                           <Skeleton width="w-20" height="h-3" rounded="rounded-md" /> {/* Skeleton for Name */}
                                           <Skeleton width="w-16" height="h-3" rounded="rounded-md" /> {/* Skeleton for Role */}
                                         </div>
                                       </div>
                                     )}
                </div>
                <ChevronDown className="h-4 w-4 opacity-70" />
              </motion.div>

              {/* Logout Button */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleLogout}
                className={`p-2 rounded-full ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
              >
                <LogOut className="h-5 w-5" />
              </motion.button>
              
              {/* Mobile Menu Toggle */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-full hover:bg-gray-800"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </motion.button>
            </div>
          </div>          
         
        </div>
      </nav>
      
      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: -300 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -300 }}
            className="fixed left-0 top-16 bottom-0 w-64 bg-gray-800 z-40 shadow-xl border-r border-gray-700 overflow-auto"
          >
            <div className="p-4">
              <div className="flex items-center space-x-3 p-3 mb-4 rounded-lg bg-gray-900/40">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-400 to-purple-500 flex items-center justify-center">
                {currentUser ? (
                      <>
                        <Avatar firstName={currentUser.first_name} lastName={currentUser.last_name} />
                      </>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Skeleton width="w-10 h-10" rounded="rounded-full" /> {/* Skeleton for Avatar */}
                        <div className="flex flex-col">
                          <Skeleton width="w-20" height="h-3" rounded="rounded-md" /> {/* Skeleton for Name */}
                          <Skeleton width="w-16" height="h-3" rounded="rounded-md" /> {/* Skeleton for Role */}
                        </div>
                      </div>
                    )}
                </div>
                <div>
                  {currentUser ? (
                                        <>                                          
                                          <div>
                                            <span className="text-sm text-gray-100 font-medium">{currentUser.first_name} {currentUser.last_name}</span>
                                            <span className="text-xs text-gray-400">{currentUser.role}</span>
                                          </div>
                                        </>
                                      ) : (
                                        <div className="flex items-center gap-2">                                         
                                          <div className="flex flex-col">
                                            <Skeleton width="w-20" height="h-3" rounded="rounded-md" /> {/* Skeleton for Name */}
                                            <Skeleton width="w-16" height="h-3" rounded="rounded-md" /> {/* Skeleton for Role */}
                                          </div>
                                        </div>
                                      )}
                </div>
              </div>
              
              <div className="space-y-1">
                {[
                  { id: 'home', icon: Home, label: 'Dashboard' },
                  { id: 'ipv4', icon: Calculator, label: 'IPv4 Suite' },
                  { id: 'ipv6', icon: Server, label: 'IPv6 Suite' },
                  { id: 'reference', icon: Grid, label: 'References' },               
                  { id: 'logout', icon: LogOut, label: 'Logout' }
                ].map((item) => (
                  <motion.button
                    key={item.id}
                    whileHover={{ x: 5 }}
                    onClick={() => {
                      // Check if the clicked item is the logout item
                      if (item.id === 'logout') {
                        handleLogout(); // Call the handleLogout function
                      } else {
                        setActiveTab(item.id);
                      }
                      setMobileMenuOpen(false); // Close the mobile menu
                    }}
                    className={`flex items-center space-x-3 w-full p-3 rounded-lg transition-all ${
                      activeTab === item.id 
                        ? 'bg-gradient-to-r from-blue-600/80 to-purple-600/80' 
                        : 'hover:bg-gray-700/50'
                    }`}
                  >
                    <item.icon className="h-5 w-5" />
                    <span>{item.label}</span>
                  </motion.button>
                ))}
              </div>
              
              <div className="mt-6 pt-6 border-t border-gray-700">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Dark Mode</span>
                  <button
                    onClick={toggleTheme}
                    className={`w-12 h-6 rounded-full relative ${darkMode ? 'bg-blue-600' : 'bg-gray-600'} transition-colors`}
                  >
                    <span className={`absolute w-5 h-5 rounded-full bg-white top-0.5 transition-transform ${darkMode ? 'right-0.5' : 'left-0.5'}`}></span>
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="container mx-auto pt-24 pb-16 px-4">
        <AnimatePresence mode="wait">
          {activeTab === 'home' && (
            <motion.div
              key="home"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-8"
            >              
               {/* Hero Section */}
               <div className={`rounded-2xl shadow-2xl p-8 ${darkMode ? 'bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700' : 'bg-gradient-to-br from-white to-gray-50 border border-gray-200'}`}>
                <div className="flex flex-col md:flex-row items-center justify-between">
                  <div className="md:w-1/2 mb-8 md:mb-0">
                    <motion.h1 
                      className="text-4xl md:text-4xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-indigo-500"
                      initial={{ y: -20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.3 }}
                    >
                      Network Intelligence toolkit
                    </motion.h1>
                    
                    <motion.p 
                      className={`text-lg mb-8 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.5 }}
                    >
                      Enterprise-grade toolkit for network architects and IT professionals.
                      Analyze addressing schemes, optimize infrastructure, and visualize your network 
                      topology with precision and clarity.
                    </motion.p>
                    
                    <div className="flex flex-wrap gap-4">
                      <motion.button
                        whileHover={{ scale: 1.05, boxShadow: "0 10px 25px -5px rgba(59, 130, 246, 0.4)" }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setActiveTab('ipv4')}
                        className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white px-6 py-3 rounded-xl flex items-center space-x-3 transition-all duration-300 shadow-lg"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.7 }}
                      >
                        <Calculator className="h-5 w-5" />
                        <span className="text-lg">IPv4 Calculator</span>
                        <ArrowRight className="h-4 w-4" />
                      </motion.button>
                      
                      <motion.button
                        whileHover={{ scale: 1.05, boxShadow: "0 10px 25px -5px rgba(16, 185, 129, 0.4)" }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setActiveTab('ipv6')}
                        className="bg-gradient-to-r from-green-500 to-teal-600 hover:from-green-600 hover:to-teal-700 text-white px-6 py-3 rounded-xl flex items-center space-x-3 transition-all duration-300 shadow-lg"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.8 }}
                      >
                        <Server className="h-5 w-5" />
                        <span className="text-lg">IPv6 Analyzer</span>
                        <ArrowRight className="h-4 w-4" />
                      </motion.button>
                    </div>
                  </div>
                  
                  <div className="md:w-1/2 flex justify-center">
                    <motion.div
                      className="relative"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.9 }}
                    >
                      <div
                        className={`absolute -inset-4 rounded-2xl ${
                          darkMode ? "bg-indigo-600/20" : "bg-indigo-100/50"
                        } blur-lg`}
                      ></div>
                      <div
                        className={`relative rounded-xl overflow-hidden ${
                          darkMode ? "bg-gray-800" : "bg-white"
                        } border ${darkMode ? "border-gray-700" : "border-gray-200"} shadow-lg`}
                      >
                        <div className="p-4">
                          <div className="flex space-x-2">
                            <div className="h-3 w-3 rounded-full bg-red-500"></div>
                            <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
                            <div className="h-3 w-3 rounded-full bg-green-500"></div>
                          </div>
                        </div>
                        <div className="px-6 pb-6 font-mono text-sm whitespace-pre-line h-[280px] overflow-y-auto">
                          {displayedLines.map((line, i) => (
                            <p
                              key={i}
                              className={
                                line.startsWith("$")
                                  ? "text-green-400"
                                  : line.startsWith("Network") || line.startsWith("Broadcast") || line.startsWith("Usable")
                                  ? "text-gray-400"
                                  : line.startsWith("Address") || line.startsWith("Range")
                                  ? "text-blue-400"
                                  : "text-gray-400"
                              }
                            >
                              {line}
                            </p>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </div>
              
              {/* Network Health Dashboard */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-xl p-6 border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold">Network Health Dashboard</h2>
                  <div className="flex space-x-2">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-800'}`}>
                      <span className="w-2 h-2 rounded-full bg-green-400 mr-1"></span>
                      Live Data
                    </span>
                    <button className={`px-3 py-1 rounded-lg text-sm ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'}`}>
                      Refresh
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  {[
                    { 
                      title: "Active Subnets", 
                      value: "32", 
                      change: "+2", 
                      icon: Wifi,
                      color: "blue"
                    },
                    { 
                      title: "IP Utilization", 
                      value: "78%", 
                      change: "+5%", 
                      icon: Activity,
                      color: "green"
                    },
                    { 
                      title: "Available IPs", 
                      value: "2,048", 
                      change: "-256", 
                      icon: Globe,
                      color: "purple"
                    },
                    { 
                      title: "Ping Status", 
                      value: "98.5%", 
                      change: "+0.5%", 
                      icon: Activity,
                      color: "indigo"
                    }
                  ].map((stat, index) => (
                    <motion.div
                      key={stat.title}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 1 + index * 0.1 }}
                      className={`${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'} rounded-xl p-6 border ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{stat.title}</p>
                          <h3 className="text-2xl font-bold mt-1">{stat.value}</h3>
                          <span className={`inline-flex items-center text-xs mt-1 ${stat.change.startsWith('+') ? 'text-green-500' : 'text-red-500'}`}>
                            {stat.change} from last week
                          </span>
                        </div>
                        <div className={`p-3 rounded-lg bg-${stat.color}-500/20`}>
                          <stat.icon className={`h-6 w-6 text-${stat.color}-500`} />
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
                
                {/* Network Visualization */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <motion.div 
                    className={`${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'} rounded-xl p-6 border ${darkMode ? 'border-gray-600' : 'border-gray-200'} lg:col-span-2`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.3 }}
                  >
                    <h3 className="text-lg font-medium mb-4">Network Traffic</h3>
                    <div className="h-64 relative">
                      {/* Chart visualization */}
                      <div className="absolute inset-0 flex items-end">
                        {networkData.map((value, index) => (
                          <div 
                            key={index} 
                            className="flex-1 mx-1"
                            style={{ height: `${value}%` }}
                          >
                            <div 
                              className={`w-full h-full rounded-t-md bg-gradient-to-t ${
                                value > 75 ? 'from-red-500 to-red-400' : 
                                value > 50 ? 'from-orange-500 to-orange-400' : 
                                'from-blue-500 to-blue-400'
                              }`}
                            ></div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex justify-between mt-4 text-xs text-gray-500">
                      <span>00:00</span>
                      <span>06:00</span>
                      <span>12:00</span>
                      <span>18:00</span>
                      <span>23:59</span>
                    </div>
                  </motion.div>
                  
                  <motion.div 
                    className={`${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'} rounded-xl p-6 border ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.4 }}
                  >
                    <h3 className="text-lg font-medium mb-4">Top Subnets</h3>
                    <div className="space-y-4">
                      {[
                        { subnet: "10.0.0.0/24", usage: 89 },
                        { subnet: "192.168.1.0/24", usage: 76 },
                        { subnet: "172.16.0.0/16", usage: 62 },
                        { subnet: "10.10.0.0/16", usage: 45 },
                      ].map((item, index) => (
                        <div key={index} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span>{item.subnet}</span>
                            <span className="font-medium">{item.usage}%</span>
                          </div>
                          <div className={`w-full h-2 rounded-full bg-gray-600/20`}>
                            <div 
                              className={`h-full rounded-full ${
                                item.usage > 80 ? 'bg-red-500' : 
                                item.usage > 60 ? 'bg-orange-500' : 
                                'bg-blue-500'
                              }`}
                              style={{ width: `${item.usage}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                </div>
              </motion.div>
              
              
              
              {/* Testimonial Section */}
              <motion.div 
                className={`${darkMode ? 'bg-gray-800/50' : 'bg-white'} rounded-2xl shadow-xl p-8 border ${darkMode ? 'border-gray-700' : 'border-gray-200'} mt-8`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2 }}
              >
                <h2 className="text-2xl font-bold text-center mb-8">Trusted by Network Professionals</h2>
                <div className="grid md:grid-cols-3 gap-6">
                  {[
                    {
                      quote: "SubnetIQ transformed how we manage our expanding cloud infrastructure. The visualization tools alone saved us countless hours.",
                      author: "F/N the_geek",
                      role: "Network Architect, TechCloud Solutions"
                    },
                    {
                      quote: "I've tried many subnet calculators, but none match the feature set and intuitive interface of SubnetIQ. It's now our team's go-to tool.",
                      author: "Muwanga Ibrahim",
                      role: "Senior Network Engineer, GlobalNet Inc."
                    },
                    {
                      quote: "The IPv6 suite is exceptional. SubnetIQ makes complex address planning straightforward even for our most challenging deployments.",
                      author: "Ana Dorin",
                      role: "Infrastructure Lead, DataCore Systems"
                    }
                  ].map((testimonial, index) => (
                    <div key={index} className={`${darkMode ? 'bg-gray-700/30' : 'bg-gray-50'} rounded-xl p-6 border ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                      <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'} italic mb-4`}>"{testimonial.quote}"</p>
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-400 to-purple-500 flex items-center justify-center text-white font-medium">
                          {testimonial.author.split(' ')[0][0]}{testimonial.author.split(' ')[1][0]}
                        </div>
                        <div className="ml-3">
                          <p className="font-medium">{testimonial.author}</p>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{testimonial.role}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>              
              
            </motion.div>
          )}
          
          {activeTab === 'ipv4' && (
            <IPv4Calculator />
          )}

          {activeTab === 'ipv6' && (
            <IPv6Calculator />
          )}

          {activeTab === 'reference' && (
            <NetworkReferenceTable />
          )}
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className={`${darkMode ? 'bg-gray-800' : 'bg-gray-100'} border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} py-12`}>
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <Network className={`h-6 w-6 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                <span className={`text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r ${primaryGradient}`}>
                  SubnetIQ
                </span>
              </div>
              <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} max-w-md mb-6`}>
                Advanced network intelligence tools for professionals. 
                Plan, visualize, and optimize your network infrastructure with precision and ease.
              </p>
              <div className="flex space-x-4">
                <a href="#" className={`p-2 rounded-full ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} transition-colors`}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M16 8.049c0-4.446-3.582-8.05-8-8.05C3.58 0-.002 3.603-.002 8.05c0 4.017 2.926 7.347 6.75 7.951v-5.625h-2.03V8.05H6.75V6.275c0-2.017 1.195-3.131 3.022-3.131.876 0 1.791.157 1.791.157v1.98h-1.009c-.993 0-1.303.621-1.303 1.258v1.51h2.218l-.354 2.326H9.25V16c3.824-.604 6.75-3.934 6.75-7.951z"/>
                  </svg>
                </a>
                <a href="#" className={`p-2 rounded-full ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} transition-colors`}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.012 8.012 0 0 0 16 8c0-4.42-3.58-8-8-8z"/>
                  </svg>
                </a>
                <a href="#" className={`p-2 rounded-full ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} transition-colors`}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708c0 .633-.526 1.146-1.175 1.146H1.175C.526 16 0 15.487 0 14.854V1.146zm4.943 12.248V6.169H2.542v7.225h2.401zm-1.2-8.212c.837 0 1.358-.554 1.358-1.248-.015-.709-.52-1.248-1.342-1.248-.822 0-1.359.54-1.359 1.248 0 .694.521 1.248 1.327 1.248h.016zm4.908 8.212V9.359c0-.216.016-.432.08-.586.173-.431.568-.878 1.232-.878.869 0 1.216.662 1.216 1.634v3.865h2.401V9.25c0-2.22-1.184-3.252-2.764-3.252-1.274 0-1.845.7-2.165 1.193v.025h-.016a5.54 5.54 0 0 1 .016-.025V6.169h-2.4c.03.678 0 7.225 0 7.225h2.4z"/>
                  </svg>
                </a>
                <a href="#" className={`p-2 rounded-full ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} transition-colors`}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M5.026 15c6.038 0 9.341-5.003 9.341-9.334 0-.14 0-.282-.006-.422A6.685 6.685 0 0 0 16 3.542a6.658 6.658 0 0 1-1.889.518 3.301 3.301 0 0 0 1.447-1.817 6.533 6.533 0 0 1-2.087.793A3.286 3.286 0 0 0 7.875 6.03a9.325 9.325 0 0 1-6.767-3.429 3.289 3.289 0 0 0 1.018 4.382A3.323 3.323 0 0 1 .64 6.575v.045a3.288 3.288 0 0 0 2.632 3.218 3.203 3.203 0 0 1-.865.115 3.23 3.23 0 0 1-.614-.057 3.283 3.283 0 0 0 3.067 2.277A6.588 6.588 0 0 1 .78 13.58a6.32 6.32 0 0 1-.78-.045A9.344 9.344 0 0 0 5.026 15z"/>
                  </svg>
                </a>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Product</h3>
              <ul className={`space-y-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                <li><a href="#" className="hover:underline">Features</a></li>
                <li><a href="#" className="hover:underline">Enterprise</a></li>
                <li><a href="#" className="hover:underline">Security</a></li>
                <li><a href="#" className="hover:underline">Pricing</a></li>
                <li><a href="#" className="hover:underline">API</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Resources</h3>
              <ul className={`space-y-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                <li><a href="#" className="hover:underline">Documentation</a></li>
                <li><a href="#" className="hover:underline">Guides</a></li>
                <li><a href="#" className="hover:underline">Webinars</a></li>
                <li><a href="#" className="hover:underline">Community</a></li>
                <li><a href="#" className="hover:underline">Blog</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Company</h3>
              <ul className={`space-y-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                <li><a href="#" className="hover:underline">About</a></li>
                <li><a href="#" className="hover:underline">Careers</a></li>
                <li><a href="#" className="hover:underline">Contact</a></li>
                <li><a href="#" className="hover:underline">Partners</a></li>
                <li><a href="#" className="hover:underline">Legal</a></li>
              </ul>
            </div>
          </div>
          
          <div className={`mt-12 pt-8 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} flex flex-col md:flex-row justify-between items-center`}>
            <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>
              &copy; {new Date().getFullYear()} 0X10LayerFF. All rights reserved.
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a href="#" className={`text-sm ${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-900'}`}>
                Privacy Policy
              </a>
              <a href="#" className={`text-sm ${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-900'}`}>
                Terms of Service
              </a>
              <a href="#" className={`text-sm ${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-900'}`}>
                Cookies
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}